import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { isPermissionDenied, isUnauthenticated } from './call.js';
import { meQueryKey } from './me.js';

/**
 * TanStack Query for the whole dashboard, with the error codes that are handled
 * globally rather than by a screen (`patterns/frontend-conventions.md`, 1):
 *
 * - `UNAUTHENTICATED` — whether reading or changing something — sends the
 *   Account User to the log in screen with the return URL, wherever they were
 *   (`ux.md`, Flow B4).
 * - `PERMISSION_DENIED` refetches `me`, so a permission removed while they are
 *   signed in also leaves the nav (`patterns/ui-patterns.md`, 3). The screen
 *   that was refused shows the permission-denied state itself.
 *
 * The handler is registered by `main.tsx` once the router exists, because it is
 * the router that performs the navigation. It never clears the cache: a query
 * whose error has just been cleared refetches on its next mount, which against
 * an ended session would refuse again.
 */
let onUnauthenticated: (() => void) | undefined;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export function createQueryClient(): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (isUnauthenticated(error)) onUnauthenticated?.();
        if (isPermissionDenied(error)) void queryClient.invalidateQueries({ queryKey: meQueryKey });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (isUnauthenticated(error)) onUnauthenticated?.();
      },
    }),
    // A refused or failed request is shown, not retried behind the Account
    // User's back: every screen state in `ux.md` says what happened.
    defaultOptions: { queries: { retry: false } },
  });
  return queryClient;
}
